import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Miniheader from '../../../../components/layout/MiniHeader/index'
import Typography from '../../../../components/common/Typography/index'
import Button from '../../../../components/common/Button/index'
import Select from '../../../../components/common/Select'
import Card from '../../../../components/common/Card/index'
import Input from '../../../../components/common/Input'
import { AccountDetailsForm, ProfileDetailsForm, PasswordUpdateForm, AddNewUserForm } from '../../../../components/forms/SettingsForm/index'
import AccordionExpandDefault from '../../../../components/common/Accordion/Accordion'
import { logout } from '../../../../shared/utils/authUtils'
import { fetchPreferences, updatePreferences, resetPreferences, confirmPreferences, sendVerifyEmail, confirmVerifyEmail, sendVerifyPhone, confirmVerifyPhone, fetchVerificationStatus, fetchCreatedAdmins, fetchAdminProfile, updateLanguagePreference } from '../../../../state/redux/actions/userActions'
import AdminEmailVerification from '../../../../components/verification/AdminEmailVerification'
import AdminPhoneVerification from '../../../../components/verification/AdminPhoneVerification'
import { FiPlusCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { MdOutlineVerified } from 'react-icons/md'
import { MdToggleOn, MdToggleOff } from 'react-icons/md'
import { FiEdit2, FiTrash2 } from 'react-icons/fi'
import { IoMdEye } from 'react-icons/io'
import { HiUser } from 'react-icons/hi'
import countries from 'world-countries'

const tabs = [
  { id: 'account', label: 'Account' },
  { id: 'security', label: 'Security' },
  { id: 'notification', label: 'Notification' },
]

const languages = [
  { value: '', label: 'Select language preference' },
  { value: 'English', label: 'English' },
  { value: 'French', label: 'French' },
  { value: 'Spanish', label: 'Spanish' },
  { value: 'German', label: 'German' },
  { value: 'Italian', label: 'Italian' },
  { value: 'Portuguese', label: 'Portuguese' },
  { value: 'Russian', label: 'Russian' },
  { value: 'Chinese', label: 'Chinese' },
  { value: 'Japanese', label: 'Japanese' },
  { value: 'Korean', label: 'Korean' },
  { value: 'Arabic', label: 'Arabic' },
]

const notificationSettings = [
  {
    title: "New users",
    description: "You'll be notified when a new user signs up",
    emailEnabled: true,
    pushEnabled: true,
  },
  {
    title: "Property",
    description: "You will be notified when agents post, sell, rent or remove properties",
    emailEnabled: true,
    pushEnabled: true,
  },
  {
    title: "Review",
    description: "You will be notified when user leaves a review",
    emailEnabled: true,
    pushEnabled: true,
  },
  {
    title: "Subscription",
    description: "You will be notified when there are new subscriptions",
    emailEnabled: true,
    pushEnabled: true,
  },
  {
    title: "Requests",
    description: "You will be notified when you get listing requests from property owner or property request",
    emailEnabled: true,
    pushEnabled: true,
  },
  {
    title: "Reports",
    description: "You will be notified when a user files a report",
    emailEnabled: true,
    pushEnabled: true,
  },
  {
    title: "Feedback",
    description: "You will be notified when feedback is submitted",
    emailEnabled: true,
    pushEnabled: true,
  },
  {
    title: "Help",
    description: "You will be notified when a user requests for help",
    emailEnabled: true,
    pushEnabled: true,
  },
  {
    title: "Activity",
    description: "You will be notified when users engage with different features on the app",
    emailEnabled: true,
    pushEnabled: true,
  },
]

const Settings = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { preferences, preferencesLoading, preferencesError, verificationStatus, verificationLoading, createdAdmins, createdAdminsLoading, createdAdminsError, adminProfile, adminProfileLoading, adminProfileError, languagePreferenceLoading, languagePreferenceError } = useSelector(state => state.user)
  // Normalize verification status payload (API returns { data: { email: {...}, phone: {...} } } or legacy flat shape)
  const _vs = verificationStatus?.data || verificationStatus || {}
  const statusEmailAddress = typeof _vs.email === 'string' ? _vs.email : _vs.email?.address
  const statusEmailVerified = (_vs.email?.verified === true) || (_vs.emailVerified === true) || false
  const statusEmailCanReVerify = _vs.email?.canReVerify ?? false
  const statusPhoneNumber = typeof _vs.phone === 'string' ? _vs.phone : _vs.phone?.number
  const statusPhoneVerified = (_vs.phone?.verified === true) || (_vs.phoneVerified === true) || false
  const statusPhoneCanReVerify = _vs.phone?.canReVerify ?? false
  const statusPhoneCanAdd = _vs.phone?.canAdd ?? false
  const statusPhoneNotSet = _vs.phone?.status === 'not_set'
  const [activeTab, setActiveTab] = useState('account')
  const [notifications, setNotifications] = useState(notificationSettings)
  const [addUser, setAddUser] = useState(false)
  const [languageValue, setLanguageValue] = useState('English')
  const [localUsers, setLocalUsers] = useState([]) // Store temporarily added users before they appear in API
  const [expandedUsers, setExpandedUsers] = useState(new Set()) // Track which users are expanded

  // Security tab states
  const [emailToVerify, setEmailToVerify] = useState('nobleyoung@ex.com')
  const [phoneNumber, setPhoneNumber] = useState('')
  // Verification UI states
  const [showEmailVerification, setShowEmailVerification] = useState(false)
  const [showPhoneVerification, setShowPhoneVerification] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [reenterPassword, setReenterPassword] = useState('')
  const [showActivityToOthers, setShowActivityToOthers] = useState(true)
  const [countryCode, setCountryCode] = useState('+971')
  const [selectedCountry, setSelectedCountry] = useState(null)
  const [showCountryDropdown, setShowCountryDropdown] = useState(false)

  // Check if current user is superadmin
  const isSuperAdmin = adminProfile?.type === 'super_admin'

  // Combine fetched created admins with locally added users
  const allUsers = useMemo(() => {
    const fetchedUsers = (createdAdmins || []).map(admin => ({
      id: admin.id || admin._id,
      firstName: admin.firstName || admin.first_name || '',
      lastName: admin.lastName || admin.last_name || '',
      email: admin.email || '',
      accessControl: admin.accessControl || admin.access_control || 'Limited',
      status: admin.status || 'Active',
      type: admin.type || 'admin',
      verifiedEmail: admin.verifiedEmail || admin.emailVerified || false,
      createdAt: admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : new Date().toLocaleDateString(),
      expanded: expandedUsers.has(admin.id || admin._id)
    }))

    // Remove local users that now appear in fetched users
    const fetchedUserEmails = new Set(fetchedUsers.map(user => user.email))
    const uniqueLocalUsers = localUsers.filter(user => !fetchedUserEmails.has(user.email))

    return [...fetchedUsers, ...uniqueLocalUsers.map(user => ({
      ...user,
      expanded: expandedUsers.has(user.id)
    }))]
  }, [createdAdmins, localUsers, expandedUsers])

  // Process countries for dropdown
  const countryOptions = countries.map(country => {
    // Handle dial codes properly - some countries have multiple suffixes
    let dialCode = ''
    if (country.idd && country.idd.root) {
      if (country.idd.suffixes && country.idd.suffixes.length > 0) {
        // Use the first suffix for simplicity, but could handle multiple
        dialCode = country.idd.root + country.idd.suffixes[0]
      } else {
        dialCode = country.idd.root
      }
    }

    return {
      name: country.name.common,
      code: country.cca2,
      cca3: country.cca3,
      dialCode: dialCode,
      flag: country.flag,
      // Add region info for better organization
      region: country.region,
      subregion: country.subregion
    }
  })
    .filter(country => country.dialCode && country.dialCode !== '+' && country.flag) // Only include countries with valid dial codes and flags
    .sort((a, b) => a.name.localeCompare(b.name))

  // Find default country (UAE)
  useEffect(() => {
    const defaultCountry = countryOptions.find(country => country.code === 'AE')
    if (defaultCountry && !selectedCountry) {
      setSelectedCountry(defaultCountry)
      setCountryCode(defaultCountry.dialCode)
    }
  }, [countryOptions, selectedCountry])

  // Close country dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryDropdown && !event.target.closest('.country-selector')) {
        setShowCountryDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showCountryDropdown])

  // Fetch preferences, verification status, created admins, and admin profile on component mount
  useEffect(() => {
    dispatch(fetchPreferences())
    // Fetch verification status on mount so UI shows current state
    dispatch(fetchVerificationStatus())
    // Fetch created admins to populate the user list persistently
    dispatch(fetchCreatedAdmins())
    // Fetch admin profile to get full user details including profile photo and language preference
    dispatch(fetchAdminProfile())
  }, [dispatch])

  // Sync language preference from admin profile to local state
  useEffect(() => {
    if (adminProfile?.preferred_language) {
      setLanguageValue(adminProfile.preferred_language)
    }
  }, [adminProfile])

  // Sync preferences with local notification state when preferences are loaded
  useEffect(() => {
    if (preferences?.notifications) {
      const updatedNotifications = notificationSettings.map(notification => {
        const preferencesKey = notification.title.toLowerCase().replace(/\s+/g, '_')
        return {
          ...notification,
          emailEnabled: preferences.notifications[preferencesKey]?.email ?? notification.emailEnabled,
          pushEnabled: preferences.notifications[preferencesKey]?.push ?? notification.pushEnabled,
        }
      })
      setNotifications(updatedNotifications)
    }
  }, [preferences])

  const signOut = () => {
    logout()
    navigate('/login')
  }

  const handleLanguageChange = async (e) => {
    const newLanguage = e.target.value
    setLanguageValue(newLanguage)

    // Only make API call if a valid language is selected (not empty)
    if (newLanguage && newLanguage !== '') {
      try {
        await dispatch(updateLanguagePreference(newLanguage))
        // Refresh admin profile to get updated language preference
        await dispatch(fetchAdminProfile())
      } catch (error) {
        console.error('Error updating language preference:', error)
        // Revert local state on error
        setLanguageValue(adminProfile?.preferred_language || '')
      }
    }
  }

  // Verification handlers
  const handleEmailVerificationSuccess = () => {
    setShowEmailVerification(false)
    // Refresh verification status
    dispatch(fetchVerificationStatus())
  }

  const handleEmailVerificationCancel = () => {
    setShowEmailVerification(false)
  }

  const handlePhoneVerificationSuccess = () => {
    setShowPhoneVerification(false)
    // Refresh verification status
    dispatch(fetchVerificationStatus())
  }

  const handlePhoneVerificationCancel = () => {
    setShowPhoneVerification(false)
  }

  const openEmailVerifierFor = (email) => {
    if (email) setEmailToVerify(email)
    setShowEmailVerification(true)
  }

  const handleUserAdded = (userData) => {
    // userData comes from the AddNewUserForm which extracts from API response
    const newUser = {
      id: userData.id || userData._id || Date.now(),
      firstName: userData.firstName || userData.first_name || '',
      lastName: userData.lastName || userData.last_name || '',
      email: userData.email || '',
      accessControl: userData.accessControl || userData.access_control || 'Limited',
      status: 'Active',
      type: userData.type || 'admin',
      verifiedEmail: userData.verifiedEmail || false,
      createdAt: userData.createdAt ? new Date(userData.createdAt).toLocaleDateString() : new Date().toLocaleDateString(),
      expanded: false
    }

    // Add to local users temporarily, it will be merged with fetched users
    setLocalUsers(prevUsers => [...prevUsers, newUser])
    setAddUser(false)

    // Optionally refresh the created admins list to get the latest from API
    // This helps ensure persistence across sessions
    dispatch(fetchCreatedAdmins())
  }

  const handleDeleteUser = (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      // Only remove from local users - fetched users should be deleted via API
      setLocalUsers(prevUsers => prevUsers.filter(user => user.id !== userId))
      // TODO: Add API call to delete user from backend if needed
    }
  }

  const toggleUserExpansion = (userId) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(userId)) {
        newSet.delete(userId)
      } else {
        newSet.add(userId)
      }
      return newSet
    })
  }

  const handleNotificationToggle = async (index, type) => {
    try {
      // Update local state first for immediate UI feedback
      const updatedNotifications = [...notifications]
      const notification = updatedNotifications[index]
      const newValue = type === 'email' ? !notification.emailEnabled : !notification.pushEnabled

      if (type === 'email') {
        updatedNotifications[index].emailEnabled = newValue
      } else {
        updatedNotifications[index].pushEnabled = newValue
      }
      setNotifications(updatedNotifications)

      // Prepare preferences update for API
      const preferencesKey = notification.title.toLowerCase().replace(/\s+/g, '_')
      const preferencesUpdate = {
        notifications: {
          ...preferences?.notifications,
          [preferencesKey]: {
            ...preferences?.notifications?.[preferencesKey],
            [type.toLowerCase()]: newValue
          }
        }
      }

      // Send update to API
      const result = await dispatch(updatePreferences(preferencesUpdate))

      if (updatePreferences.rejected.match(result)) {
        // Revert local state if API call fails
        if (type === 'email') {
          updatedNotifications[index].emailEnabled = !newValue
        } else {
          updatedNotifications[index].pushEnabled = !newValue
        }
        setNotifications(updatedNotifications)
        console.error('Failed to update preferences:', result.payload)
      }
    } catch (error) {
      console.error('Error updating notification preference:', error)
      // Revert the change on error
      const revertedNotifications = [...notifications]
      setNotifications(revertedNotifications)
    }
  }

  const handleResetPreferences = async () => {
    try {
      const result = await dispatch(resetPreferences({
        confirmReset: true,
        categories: ['notifications']
      }))

      if (resetPreferences.fulfilled.match(result)) {
        // Reset local notifications state to default
        setNotifications(notificationSettings)
      } else {
        console.error('Failed to reset preferences:', result.payload)
      }
    } catch (error) {
      console.error('Error resetting preferences:', error)
    }
  }

  return (
    <>
      <Miniheader title="Settings" signOut={signOut} />
      <div className="ml-[250px] p-8 max-w-7xl">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`px-6 py-3 font-medium text-sm border-b-2 mr-8 ${activeTab === tab.id
                  ? 'text-teal-600 border-teal-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Account Tab */}
        {activeTab === 'account' && !addUser && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column - Profile Form */}
            <div className="space-y-6">
              {/* Profile Details */}
              <ProfileDetailsForm />

              {/* Language Selection */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <Typography className="text-gray-600 text-sm mb-4">
                  Select from multiple languages for a more inclusive platform
                </Typography>
                <div className="relative">
                  <Select
                    options={languages}
                    value={languageValue}
                    onChange={handleLanguageChange}
                    className="w-full"
                    disabled={languagePreferenceLoading}
                  />
                  {languagePreferenceLoading && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
                    </div>
                  )}
                </div>
                {languagePreferenceError && (
                  <div className="mt-2 text-red-600 text-sm">
                    {languagePreferenceError}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - User Management */}
            <div className="bg-white p-6 rounded-lg border border-gray-200">
              <Typography className="text-gray-700 mb-4">
                Quickly onboard users by adding their details and assigning permissions
              </Typography>

              {isSuperAdmin ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setAddUser(true)}
                    className="border-2 border-gray-300 text-gray-500 px-4 py-2 rounded-md hover:bg-gray-50 mb-6"
                  >
                    <FiPlusCircle className="mr-2" />
                    Add New User
                  </Button>

                  {/* User List */}
                  <div className="space-y-2">
                    <Typography variant="h4" className="mb-4">Added Users ({allUsers.length})</Typography>
                    {createdAdminsLoading ? (
                      <div className="text-center py-8 text-gray-500">
                        <Typography variant="body" className="text-gray-500">
                          Loading created admins...
                        </Typography>
                      </div>
                    ) : createdAdminsError ? (
                      <div className="text-center py-8 text-red-500">
                        <Typography variant="body" className="text-red-500">
                          Error loading admins: You have insufficient privileges
                        </Typography>
                      </div>
                    ) : allUsers.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <Typography variant="body" className="text-gray-500">
                          No users added yet. Click "Add New User" to create your first admin user.
                        </Typography>
                      </div>
                    ) : (
                  allUsers.map((user) => (
                    <div key={user.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleUserExpansion(user.id)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <HiUser className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900 text-base">
                              {user.firstName} {user.lastName}
                            </span>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-500">
                                {user.email}
                              </span>
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">
                                {user.status}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-400 capitalize">{user.type}</span>
                          {user.expanded ? (
                            <FiChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <FiChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {user.expanded && (
                        <div className="px-4 pb-4 bg-gray-50 border-t">
                          <div className="grid grid-cols-2 gap-4 mb-4">
                            <div>
                              <Typography variant="body" className="text-gray-500 text-sm mb-1">Email</Typography>
                              <Typography variant="body" className="text-gray-800">{user.email}</Typography>
                            </div>
                            <div>
                              <Typography variant="body" className="text-gray-500 text-sm mb-1">Access Control</Typography>
                              <Typography variant="body" className="text-gray-800">{user.accessControl}</Typography>
                            </div>
                            <div>
                              <Typography variant="body" className="text-gray-500 text-sm mb-1">Status</Typography>
                              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">{user.status}</span>
                            </div>
                            <div>
                              <Typography variant="body" className="text-gray-500 text-sm mb-1">Date Added</Typography>
                              <Typography variant="body" className="text-gray-800">{user.createdAt}</Typography>
                            </div>
                            <div>
                              <Typography variant="body" className="text-gray-500 text-sm mb-1">User Type</Typography>
                              <Typography variant="body" className="text-gray-800 capitalize">{user.type}</Typography>
                            </div>
                            <div>
                              <Typography variant="body" className="text-gray-500 text-sm mb-1">Email Verified</Typography>
                              <span className={`px-2 py-1 text-xs rounded-full ${user.verifiedEmail
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                {user.verifiedEmail ? 'Verified' : 'Pending'}
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                            <div className="space-x-2">
                              <Button
                                size="small"
                                className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                Delete user
                              </Button>
                              <Button
                                size="small"
                                className="bg-teal-500 text-white px-4 py-1 rounded hover:bg-teal-600"
                              >
                                Change control
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    ))
                  )}
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <div className="mb-4">
                    <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <Typography variant="h4" className="mb-2 text-gray-900">
                    Access Restricted
                  </Typography>
                  <Typography variant="body" className="text-gray-600 mb-4">
                    Only Super Admins can view and manage added users. Please contact your Super Admin for assistance.
                  </Typography>
                  <div className="inline-flex items-center px-4 py-2 bg-gray-100 rounded-full">
                    <span className="text-sm text-gray-700">Your Role: </span>
                    <span className="ml-1 text-sm font-medium text-gray-900 capitalize">
                      {adminProfile?.type?.replace('_', ' ') || 'Admin'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Add User Form */}
        {activeTab === 'account' && addUser && isSuperAdmin && (
          <div className="flex items-center justify-center">
            <AddNewUserForm
              onSuccess={handleUserAdded}
              onCancel={() => setAddUser(false)}
            />
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Email Verification - only show when user is not verified */}
              {!(verificationLoading ? false : (statusEmailVerified && !statusEmailCanReVerify)) && (
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <Typography variant="h4" className="mb-4 text-gray-900">
                    Verify email
                  </Typography>
                  {verificationLoading ? (
                    <div className="text-center py-4">
                      <Typography variant="body" className="text-gray-600">Loading verification status...</Typography>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <span className="text-gray-700">Your email </span>
                          <span className="text-teal-600">{statusEmailAddress || emailToVerify}</span>
                          {statusEmailVerified ? (
                            statusEmailCanReVerify ? (
                              <span className="text-gray-700"> is verified — you may re-verify if you want</span>
                            ) : (
                              <span className="text-gray-700"> is verified</span>
                            )
                          ) : (
                            <span className="text-gray-700"> isn't verified yet</span>
                          )}
                        </div>
                      </div>
                      <div className="space-x-2">
                        <Button size="small" className="bg-teal-600 text-white px-4 py-1 rounded" onClick={() => setShowEmailVerification(true)}>
                          {statusEmailVerified ? (statusEmailCanReVerify ? 'Re-verify' : 'Verified') : 'Verify now'}
                        </Button>
                        <Button size="small" variant="outline" className="border border-gray-300 px-4 py-1 rounded text-gray-700">
                          Update
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Text Message Verification */}
              {/* Phone verification - show when phone not set, not verified, or can be re-verified */}
              {!(verificationLoading ? false : (statusPhoneVerified && !statusPhoneCanReVerify)) && (
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <Typography variant="h4" className="mb-4 text-gray-900">
                    Text message
                  </Typography>
                  <Typography className="text-gray-700 mb-4">
                    {statusPhoneNotSet
                      ? "Add your mobile number to receive verification codes for secure login. Keep your account safe!"
                      : "When we detect a login from an unknown device, we'll send a verification code to this mobile number. Keep your account safe!"
                    }
                  </Typography>
                  {verificationLoading ? (
                    <div className="text-center py-4">
                      <Typography variant="body" className="text-gray-600">Loading verification status...</Typography>
                    </div>
                  ) : (
                    <>
                      <div className="mb-4">
                        <label className="block text-sm text-gray-700 mb-2">Phone Number</label>
                        <div className="flex gap-2">
                          {/* Country Code Selector */}
                          <div className="relative country-selector">
                            <button
                              type="button"
                              onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                              className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 min-w-[140px] transition-colors"
                            >
                              {selectedCountry ? (
                                <>
                                  <div className="flex-shrink-0 relative z-0">
                                    <img
                                      src={`https://flagcdn.com/16x12/${selectedCountry.code.toLowerCase()}.png`}
                                      alt={selectedCountry.name}
                                      className="w-4 h-3 rounded-sm border border-gray-200 relative z-0"
                                      onError={(e) => {
                                        // Fallback to emoji flag
                                        e.target.style.display = 'none'
                                        e.target.nextElementSibling.style.display = 'inline'
                                      }}
                                    />
                                    <span
                                      className="text-lg hidden z-0"
                                      style={{ display: 'none' }}
                                    >
                                      {selectedCountry.flag}
                                    </span>
                                  </div>
                                  <span className="text-sm font-medium">{selectedCountry.dialCode}</span>
                                  <span className="text-xs text-gray-500 hidden sm:block">{selectedCountry.code}</span>
                                </>
                              ) : (
                                <span className="text-sm text-gray-500">Select country</span>
                              )}
                              <FiChevronDown className={`w-4 h-4 transition-transform ${showCountryDropdown ? 'rotate-180' : ''} ml-auto`} />
                            </button>

                            {/* Country Dropdown */}
                            {showCountryDropdown && (
                              <div className="absolute top-full left-0 mt-1 w-96 max-h-72 overflow-y-auto bg-white border border-gray-300 rounded-md shadow-lg z-50">
                                <div className="sticky top-0 bg-gray-50 px-3 py-2 border-b border-gray-200 z-10 relative">
                                  <span className="text-xs font-medium text-gray-500">Select Country ({countryOptions.length} countries)</span>
                                </div>
                                {countryOptions.map((country) => (
                                  <button
                                    key={country.code}
                                    type="button"
                                    onClick={() => {
                                      setSelectedCountry(country)
                                      setCountryCode(country.dialCode)
                                      setShowCountryDropdown(false)
                                    }}
                                    className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-teal-50 border-b border-gray-100 last:border-b-0 transition-colors"
                                  >
                                    <div className="flex-shrink-0 relative z-0">
                                      <img
                                        src={`https://flagcdn.com/16x12/${country.code.toLowerCase()}.png`}
                                        alt={country.name}
                                        className="w-5 h-4 rounded-sm border border-gray-200 relative z-0"
                                        onError={(e) => {
                                          // Fallback to emoji flag
                                          e.target.style.display = 'none'
                                          e.target.nextElementSibling.style.display = 'inline-block'
                                        }}
                                      />
                                      <span
                                        className="text-lg absolute top-0 left-0 hidden z-0"
                                        style={{ display: 'none' }}
                                      >
                                        {country.flag}
                                      </span>
                                    </div>
                                    <div className="flex flex-col flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-900">{country.name}</span>
                                        <span className="text-xs text-gray-500 uppercase">{country.code}</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs font-medium text-teal-600">{country.dialCode}</span>
                                        {country.region && (
                                          <span className="text-xs text-gray-400">• {country.region}</span>
                                        )}
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          <Input
                            placeholder={statusPhoneNotSet ? "Enter phone number" : "Enter here"}
                            // Prefer the local editable phoneNumber state when the user types
                            // otherwise show the value from verification status (if available)
                            // For "Not set" status, show empty placeholder
                            value={phoneNumber !== '' ? phoneNumber : (statusPhoneNotSet ? '' : (statusPhoneNumber || ''))}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            className="flex-1"
                          />
                        </div>
                        {statusPhoneNotSet && (
                          <Typography className="text-xs text-gray-500 mt-1">
                            You can add a phone number to enhance account security
                          </Typography>
                        )}
                      </div>

                      <div className="space-x-2">
                        <Button
                          size="small"
                          className={`px-4 py-1 rounded ${statusPhoneNotSet && !phoneNumber.trim()
                              ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                              : 'bg-teal-600 text-white hover:bg-teal-700'
                            }`}
                          onClick={() => {
                            if (statusPhoneNotSet && !phoneNumber.trim()) {
                              alert('Please enter a phone number first')
                              return
                            }
                            setShowPhoneVerification(true)
                          }}
                          disabled={statusPhoneNotSet && !phoneNumber.trim()}
                        >
                          {statusPhoneNotSet
                            ? 'Add & Verify'
                            : statusPhoneVerified
                              ? (statusPhoneCanReVerify ? 'Re-verify' : 'Verified')
                              : 'Verify'
                          }
                        </Button>
                        <Button size="small" className="bg-red-500 text-white px-4 py-1 rounded hover:bg-red-600">
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Change Password */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <Typography variant="h4" className="mb-6 text-gray-900">
                  Change current password
                </Typography>

                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Current Password</label>
                    <div className="relative">
                      <Input
                        type="password"
                        placeholder="Jeremy12"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        className="w-full pr-10"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <IoMdEye className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">New Password</label>
                    <div className="relative">
                      <Input
                        type="password"
                        placeholder="•••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full pr-10"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <IoMdEye className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-700 mb-2">Re-enter Password</label>
                    <div className="relative">
                      <Input
                        type="password"
                        placeholder="•••••••"
                        value={reenterPassword}
                        onChange={(e) => setReenterPassword(e.target.value)}
                        className="w-full pr-10"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        <IoMdEye className="h-5 w-5 text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>

                <Button className="bg-teal-600 text-white px-8 py-2 rounded-full hover:bg-teal-700 w-full">
                  Update Now
                </Button>
              </div>

              {/* Activity Visibility */}
              <div className="bg-white p-6 rounded-lg border border-gray-200">
                <Typography variant="h4" className="mb-4 text-gray-900">
                  Choose whether to show your activity to others or remain private
                </Typography>

                <div className="flex items-center justify-between">
                  <Typography className="text-gray-700">
                    Inform others of your online availability
                  </Typography>
                  <button
                    onClick={() => setShowActivityToOthers(!showActivityToOthers)}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                    style={{ backgroundColor: showActivityToOthers ? '#0d9488' : '#d1d5db' }}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showActivityToOthers ? 'translate-x-6' : 'translate-x-1'
                        }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notification Tab */}
        {activeTab === 'notification' && (
          <div className="bg-white p-6 rounded-lg border border-gray-200 max-w-4xl">
            {/* Header with Reset Button */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <Typography variant="h4" className="text-gray-900 mb-2">
                  Notification Preferences
                </Typography>
                <Typography className="text-gray-600 text-sm">
                  Manage how you receive notifications for different activities
                </Typography>
              </div>
              <Button
                variant="outline"
                size="small"
                onClick={handleResetPreferences}
                disabled={preferencesLoading}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50"
              >
                {preferencesLoading ? 'Resetting...' : 'Reset to Default'}
              </Button>
            </div>

            {/* Error Display */}
            {preferencesError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <Typography className="text-red-800 text-sm">
                  Error: {typeof preferencesError === 'string' ? preferencesError : 'An error occurred'}
                </Typography>
              </div>
            )}

            {/* Loading State */}
            {preferencesLoading && (
              <div className="mb-4 text-center">
                <Typography className="text-gray-500">Loading preferences...</Typography>
              </div>
            )}

            <div className="space-y-6">
              {notifications.map((notification, index) => (
                <div key={index} className="flex items-start justify-between py-4 border-b border-gray-100 last:border-b-0">
                  <div className="flex-1">
                    <Typography variant="h4" className="text-gray-900 mb-1">
                      {notification.title}
                    </Typography>
                    <Typography className="text-gray-600 text-sm">
                      {notification.description}
                    </Typography>
                  </div>

                  <div className="flex flex-col items-end space-y-2 ml-6">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-700">Email</span>
                      <button
                        onClick={() => handleNotificationToggle(index, 'email')}
                        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                        style={{ backgroundColor: notification.emailEnabled ? '#0d9488' : '#d1d5db' }}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.emailEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                      </button>
                    </div>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-700">Push</span>
                      <button
                        onClick={() => handleNotificationToggle(index, 'push')}
                        className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                        style={{ backgroundColor: notification.pushEnabled ? '#0d9488' : '#d1d5db' }}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${notification.pushEnabled ? 'translate-x-6' : 'translate-x-1'
                            }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Email Verification Modal */}
      {showEmailVerification && (
        <AdminEmailVerification
          onSuccess={handleEmailVerificationSuccess}
          onCancel={handleEmailVerificationCancel}
        />
      )}

      {/* Phone Verification Modal */}
      {showPhoneVerification && (
        <AdminPhoneVerification
          onSuccess={handlePhoneVerificationSuccess}
          onCancel={handlePhoneVerificationCancel}
          targetPhone={phoneNumber}
          countryCode={countryCode}
          isNewPhoneAdd={statusPhoneNotSet}
        />
      )}
    </>
  )
}

export default Settings